import React, { useRef, useCallback, useMemo, memo, useEffect } from 'react';
import useDimensions from 'react-use-dimensions';
import shallow from 'zustand/shallow';
import styled from 'styled-components';
import { scaleLinear } from 'd3-scale';
import { sum } from 'd3-array';
import { margin } from '~/../config.json';
import useStore from '~/store';
import { maxData, sumDatum, resizeIframeDebounced } from '~/utils';

import XAxis from './XAxis';
import YAxis from './YAxis';
import DatumHighlight from './DatumHighlight';
import ChartNotification from './ChartNotification';

const ChartSvg = styled.svg`
  display: block;
`;

const ChartWrapperDiv = styled.div`
  width: 100%;
`;

const ChartDiv = styled.div`
  position: relative;
  width: 100%;
`;

const RectItem = styled.rect`
  shape-rendering: crispEdges;
  stroke: rgba(255, 255, 255, 0.5);
  stroke-width: 0;

  &:hover {
    stroke: #111;
    stroke-width: 2;
  }
`;

function Bar({
  height,
  data,
  columns,
  scale,
  invert,
  width,
  onMouseEnter = () => {},
  onMouseLeave = () => {},
  sheet,
  columnsLabel,
}) {
  const rowSum = sumDatum(
    data,
    columns.map((col) => col.name)
  );
  const barWidth = scale(rowSum);
  const x = invert ? width - barWidth : 0;

  return (
    <g transform={`translate(${x}, 0)`}>
      {columns.map((col, i) => (
        <RectItem
          fill={col.color}
          x={scale(sum(columns.slice(0, i), (c) => data[c.name]))}
          y={0}
          width={scale(data[col.name])}
          height={height}
          onMouseEnter={() =>
            onMouseEnter({ columnName: col.name, color: col.color, data, sheet, columnsLabel, sum: rowSum })
          }
          onMouseLeave={onMouseLeave}
        />
      ))}
    </g>
  );
}

const storeSelector = (s) => ({
  leftData: s.leftData,
  rightData: s.rightData,
  setTooltipPosition: s.setTooltipPosition,
  setTooltipData: s.setTooltipData,
  setTooltipVisible: s.setTooltipVisible,
  leftColumnsId: s.leftColumnsId,
  rightColumnsId: s.rightColumnsId,
  leftSheet: s.leftSheet,
  rightSheet: s.rightSheet,
  leftColumnsLabel: s.leftColumnsLabel,
  rightColumnsLabel: s.rightColumnsLabel,
  currentView: s.currentView,
  leftLoading: s.leftLoading,
  rightLoading: s.rightLoading,
  dynamicXAxis: s.dynamicXAxis,
});

const leftColumnsSelector = (s) => s.leftColumns.filter((col) => col.isActive).reverse();
const rightColumnsSelector = (s) => s.rightColumns.filter((col) => col.isActive);
// const rightColumnsSelectorReversed = (s) => s.rightColumns.filter((col) => col.isActive).reverse();

const Chart = memo(({ outerWidth, outerHeight }) => {
  const svgRef = useRef();

  const {
    leftData,
    rightData,
    setTooltipPosition,
    setTooltipData,
    setTooltipVisible,
    leftSheet,
    rightSheet,
    leftColumnsLabel,
    rightColumnsLabel,
    currentView,
    leftLoading,
    rightLoading,
    dynamicXAxis,
  } = useStore(storeSelector, shallow);

  const isLoading = leftLoading || rightLoading;

  // const rightColumnsSelector =
  //   leftColumnsId === rightColumnsId ? rightColumnsSelectorReversed : rightColumnsSelectorDefault;

  const leftColumns = useStore(leftColumnsSelector, shallow);
  const rightColumns = useStore(rightColumnsSelector, shallow);

  const width = outerWidth - margin.left - margin.right - margin.center;
  const height = outerHeight - margin.top - margin.bottom;

  const maxLeft = maxData(leftData, leftColumns);
  const maxRight = maxData(rightData, rightColumns);
  const maxAge = 85;
  const maxValue = Math.max(maxLeft, maxRight);
  const maxOverall = dynamicXAxis // eslint-disable-line
    ? maxValue // eslint-disable-line
    : currentView.forceX // eslint-disable-line
    ? currentView.xMax // eslint-disable-line
    : Math.max(maxValue, currentView.xMax); // eslint-disable-line

  const xScale = useMemo(
    () =>
      scaleLinear()
        .domain([0, maxOverall])
        .range([0, (width - margin.center / 2) / 2]),
    [maxOverall, width]
  );

  const barHeight = useMemo(() => height / maxAge, [height, maxAge]);

  const yScale = useMemo(
    () =>
      scaleLinear()
        .domain([0, maxAge])
        .range([height - barHeight, 0]),
    [maxAge, height, barHeight]
  );

  const handleMouseMove = useCallback((evt) => {
    const wrapperBounds = svgRef.current?.getBoundingClientRect();

    if (wrapperBounds) {
      const xPos = evt.clientX - wrapperBounds.left;
      const yPos = evt.clientY - wrapperBounds.top;
      setTooltipPosition([xPos, yPos]);
    }
  }, []);

  const handleMouseEnter = useCallback((d) => {
    setTooltipVisible(true);
    setTooltipData(d);
  }, []);

  const handleMouseLeave = useCallback(() => {
    setTooltipVisible(false);
  }, []);

  return (
    <ChartDiv>
      <ChartSvg ref={svgRef} width={outerWidth} height={outerHeight} onMouseMove={handleMouseMove}>
        <g transform={`translate(${margin.left}, ${margin.top})`}>
          <YAxis width={width} height={height} barHeight={barHeight} yScale={yScale} />
          <XAxis width={width} height={height} xScale={xScale} />
          <g>
            {!isLoading &&
              leftData.map((d) => (
                <g transform={`translate(0, ${yScale(d.Alter)})`}>
                  <g>
                    <Bar
                      data={d}
                      columns={leftColumns}
                      scale={xScale}
                      invert
                      width={width / 2}
                      height={barHeight}
                      onMouseEnter={handleMouseEnter}
                      onMouseLeave={handleMouseLeave}
                      sheet={leftSheet}
                      columnsLabel={leftColumnsLabel}
                    />
                  </g>
                </g>
              ))}
            {!isLoading &&
              rightData.map((d) => (
                <g transform={`translate(0, ${yScale(d.Alter)})`}>
                  <g transform={`translate(${width / 2 + margin.center}, 0)`}>
                    <Bar
                      data={d}
                      columns={rightColumns}
                      scale={xScale}
                      height={barHeight}
                      onMouseEnter={handleMouseEnter}
                      onMouseLeave={handleMouseLeave}
                      sheet={rightSheet}
                      columnsLabel={rightColumnsLabel}
                    />
                  </g>
                </g>
              ))}
          </g>
          {!isLoading && (
            <DatumHighlight outerWidth={outerWidth} width={width} height={barHeight} xScale={xScale} yScale={yScale} />
          )}
        </g>
      </ChartSvg>
      {leftSheet.group === 'Bildung' && (
        <ChartNotification text="ohne Minderjährige in dieser Auswahl" x={outerWidth / 4} y={yScale(13) + margin.top} />
      )}
      {rightSheet.group === 'Bildung' && (
        <ChartNotification
          text="ohne Minderjährige in dieser Auswahl"
          x={(outerWidth / 4) * 3}
          y={yScale(13) + margin.top}
        />
      )}
    </ChartDiv>
  );
});

export default function ChartWrapper() {
  const [ref, { width }] = useDimensions();
  const aspectRatio = width < 500 ? 1.15 : 0.85;
  const height = width * aspectRatio;

  useEffect(() => {
    resizeIframeDebounced();
  }, [width]);

  return (
    <ChartWrapperDiv style={{ width: '100%' }} ref={ref}>
      {width > 0 && <Chart outerWidth={width} outerHeight={height} />}
    </ChartWrapperDiv>
  );
}
